<template>
  <div>
    <div
      data-cy="form-label"
      class="b-dialog__top-panel mb-4"
    >
      <h2>{{ $t('CONTENT.contract.contract_editing') }}</h2>
      <button
        class="b-btn b-btn_border_none"
        @click="closeContractEditingDialog"
      >
        <dynamic-icon icon="close" />
      </button>
    </div>
    <form class="b-form b-dialog__form">
      <server-error :error="error" />
      <div class="row px-0">
        <div class="col-4">
          <label for="number">{{ $t('CONTENT.contract.contract_number') }}:<span class="b-form__required"> *</span></label>
          <input
            id="number"
            v-model.lazy="formData.number"
            data-cy="contract-number"
            type="text"
            class="b-input"
            name="number"
            required
            :class="{ 'b-form__field-error': isFieldWithError('number', formData.number) }"
          >
        </div>
        <div class="col-4">
          <label>{{ $t('CONTENT.date') }}:<span class="b-form__required"> *</span></label>
          <datepicker
            v-model="date"
            :class="{ 'b-datepicker__error': isFieldWithError('date', date) }"
            class="b-datepicker"
            :formatter="datepickerFormat"
          />
        </div>
        <div class="col-4">
          <label for="externalId">{{ $t('CONTENT.external_id') }}:</label>
          <input
            id="externalId"
            v-model.lazy="formData.externalId"
            data-cy="contract-externalId"
            type="text"
            class="b-input"
            name="externalId"
          >
        </div>
      </div>
      <div class="row mt-5 px-0 b-form__row_type_flex-end">
        <div class="col-8">
          <div class="b-form__container">
            <label>{{ $t('CONTENT.contract.lessee') }}:<span class="b-form__required"> *</span></label>
            <cool-select
              v-model="currentLessee"
              :list="counterparties"
              option-value="id"
              option-text="value"
              :placeholder="$t('CONTENT.choose')"
              :is-error="isFieldWithError('currentLessee', currentLessee)"
            />
          </div>
        </div>
        <div
          v-if="checkIsCounterpartyIntragroup(currentLessee)"
          class="b-contract__check mx-2 mb-2"
        >
          <dynamic-icon
            class="mr-2"
            icon="check"
          />
          {{ $t('CONTENT.counterparty.VGO') }}
        </div>
      </div>
      <div class="row mt-5">
        <div class="col-3">
          <div class="b-form__container">
            <label>{{ $t('CONTENT.contract.lessor') }}:<span class="b-form__required"> *</span></label>
          </div>
        </div>
      </div>
      <div :key="counter">
        <div
          v-for="(item, index) in formData.lessorIds"
          :key="index"
        >
          <div class="row px-0 b-form__container_type_flex">
            <div class="col-12">
              <LessorItemEdit
                :lessor="item"
                :lessors-length="formData.lessorIds.length"
                :is-error="formData.lessorIds && formData.lessorIds.length - 1 === index ? isFieldWithError('lessor', item) : false"
                :lessor-index="index"
                @deleteLessor="deleteLessorItem"
                @selectLessor="onSelectLessor($event)"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-8">
          <div class="b-form__container">
            <button
              type="button"
              class="b-btn"
              data-cy="add-lessor"
              :disabled="isDisabledAddLessor"
              @click="addLessor"
            >
              <dynamic-icon
                class="b-btn__icon b-btn__icon_color_main-text"
                icon="plus"
              />
              {{ $t('CONTENT.contract.add_lessor') }}
            </button>
          </div>
        </div>
      </div>
      <div class="row mt-5 px-0 b-form__row_type_flex-end">
        <div class="col-8">
          <label for="responsible">{{ $t('CONTENT.contract.initiator') }}:</label>
          <input
            id="responsible"
            v-model.lazy="formData.responsible"
            data-cy="contract-resonsible"
            type="text"
            class="b-input"
            name="responsible"
          >
        </div>
      </div>
      <div class="row mt-5">
        <div class="col-12">
          <checkbox
            id="isLowValue"
            v-model="formData.isLowValue"
            class="b-checkbox"
            :disabled="formData.isNotRentContract === true"
          >
            {{ $t('CONTENT.contract.is_low_value') }}
          </checkbox>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <checkbox
            id="isExceptionToArticle3OfIFRS16"
            v-model="formData.isExceptionToArticle3OfIFRS16"
            class="b-checkbox"
            :disabled="formData.isNotRentContract === true"
          >
            {{ $t('CONTENT.contract.isExceptionToArticle3OfIFRS16') }}
          </checkbox>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <checkbox
            id="isShortTerm"
            v-model="formData.isShortTerm"
            class="b-checkbox"
            :disabled="formData.isNotRentContract === true"
          >
            {{ $t('CONTENT.contract.isShortTerm') }}
          </checkbox>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <checkbox
            id="isIrrelevant"
            v-model="formData.isIrrelevant"
            class="b-checkbox"
            :disabled="formData.isNotRentContract === true"
          >
            {{ $t('CONTENT.contract.isIrrelevant') }}
          </checkbox>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <checkbox
            id="isVariableLeasePayments"
            v-model="formData.isVariableLeasePayments"
            class="b-checkbox"
            :disabled="formData.isNotRentContract === true"
          >
            {{ $t('CONTENT.contract.isVariableLeasePayments') }}
          </checkbox>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <checkbox
            id="isNotRentContract"
            v-model="formData.isNotRentContract"
            class="b-checkbox"
          >
            {{ $t('CONTENT.contract.isNotRentContract') }}
          </checkbox>
        </div>
      </div>
      <div class="row mt-2 b-contract__type-exception-error" v-if="isFieldWithError('typeException', false) && !isSelectedTypeException">
        <div class="col-5">
          <p>Необходимо выбрать вид исключения</p>
        </div>
      </div>
      <div v-if="isShowAssetsForAdd && formData.periodSpendings.length !== 0">
        <div v-for="(lowValueAsset, lowValueAssetsIndex) in formData.lowValueAssets">
          <LowValueAssetsItemEdit
            :low-value-asset="lowValueAsset"
            :low-value-assets-index="lowValueAssetsIndex"
            :settings-currency-code="settingsCurrencyCode"
            :available-asset-ids-for-group="availableAssetIds"
            :low-value-assets-length="formData.lowValueAssets.length"
            :validation-errors="validationLowValueAsset[lowValueAssetsIndex]"
            @newAssetSelected="newAssetSelectedForGroup"
            @newAssetCreated="newAssetCreatedForGroup"
            @deleteAsset="onDeleteAsset"
          />
        </div>
        <div class="row mt-5">
          <div class="col-5">
            <div class="b-form__container">
              <button
                type="button"
                class="b-btn"
                :disabled="isDisabledSaveLowValueAssetsEdit"
                @click="addLowValueAsset"
              >
                <dynamic-icon
                  class="b-btn__icon b-btn__icon_color_main-text"
                  icon="plus"
                />
                {{ $t('CONTENT.assets.add_asset') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isShowPeriodSpendings && formData.lowValueAssets.length !== 0">
        <div
          v-for="(item, index) in formData.periodSpendings"
          :key="index"
        >
          <PeriodSpendingsItem
            :period-spendings-item="item"
            :period-spendings-item-index="index"
            :period-spendings-length="formData.periodSpendings.length"
            :system-currency-code="settingsCurrencyCode"
            :validation-errors="validationPeriodSpendings[index]"
            @onDeletePeriodSpending="deletePeriodSpendingsItem"
          />
        </div>
        <div class="row mt-4">
          <div class="col-5">
            <div class="b-form__container">
              <button
                type="button"
                class="b-btn"
                :disabled="isDisabledAddPeriodSpendings"
                @click="addPeriodSpendings"
              >
                <dynamic-icon
                  class="b-btn__icon b-btn__icon_color_main-text"
                  icon="plus"
                />
                {{ $t('CONTENT.contract.add_period') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-5">
        <div class="col-6 offset-6">
          <div
            class="b-form__container b-form__container_type_flex b-form__container_type_flex_end"
          >
            <button
              data-cy="save-button"
              class="b-btn b-btn_type_primary"
              type="submit"
              @click="onSubmit"
            >
              {{ $t('CONTENT.save') }}
            </button>
            <button
              class="b-btn b-btn_type_empty m-2"
              @click.prevent="closeContractEditingDialog"
            >
              {{ $t('CONTENT.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import dateConverter from '../../../../../utils/converters/date.converter';
import LessorItemEdit from './LessorItemEdit';
import LowValueAsset from '../../../../../models/alternativeContract/LowValueAsset';
import LowValueAssetsItemEdit from './LowValueAssetsItemEdit';
import PeriodSpendings from '../../../../../models/alternativeContract/PeriodSpendings';
import PeriodSpendingsItem from '../../PeriodSpendingsItem';
import datepickerFormat from '@/mixins/datepickerFormat.mixin';

export default {
  components: {
    LessorItemEdit,
    LowValueAssetsItemEdit,
    PeriodSpendingsItem,
  },
  mixins: [datepickerFormat],
  props: {
    contract: {
      type: Object,
    },
  },
  data() {
    return {
      error: null,
      counter: 0,
      isContractEditingShow: false,
      isDisabledAddLessor: false,
      formData: {},
      availableAssetIds: [],
      isShowPeriodSpendings: false,
      isShowAssetsForAdd: false,
      validationErrors: [],
      validationPeriodSpendings: [],
      validationLowValueAsset: [],
    };
  },
  watch: {
    contract: {
      immediate: true,
      handler() {
        this.updateFormData();
      },
    },
    'formData.isLowValue': {
      handler() {
        this.setIsShowAssetsForAdd();
      },
      deep: true,
    },
    'formData.isShortTerm': {
      handler() {
        this.setIsShowAssetsForAdd();
      },
      deep: true,
    },
    'formData.isVariableLeasePayments': {
      handler() {
        this.setIsShowAssetsForAdd();
      },
      deep: true,
    },
    'formData.isNotRentContract': {
      handler(value) {
        if (value) {
          this.formData.isExceptionToArticle3OfIFRS16 = false;
          this.formData.isIrrelevant = false;
          this.formData.isLowValue = false;
          this.formData.isShortTerm = false;
          this.formData.isVariableLeasePayments = false;
        }
      },
    },
    isShowAssetsForAdd(newVal) {
      if (this.formData.periodSpendings.length !== 0) {
        if (newVal) {
          this.isShowLowValueAssets = true;
          this.formData.lowValueAssets = [];
          this.formData.lowValueAssets.push(LowValueAsset.createNew());
        } else {
          this.isShowLowValueAssets = false;
        }
      } else if (this.formData.lowValueAssets.length !== 0) {
        if (newVal) {
          this.isShowLowValueAssets = false;
        } else {
          this.isShowPeriodSpendings = true;
          this.formData.periodSpendings = [];
          this.formData.periodSpendings.push(PeriodSpendings.createNew());
        }
      }
    },
  },
  created() {
    this.updateFormData();

    if (this.availableAssetIds.length === 0) {
      this.availableAssetIds = [...this.getAssets];
    }
  },
  computed: {
    ...mapGetters(['getCounterpartiresForContract']),
    ...mapGetters(['allCounterparties']),
    ...mapGetters(['getContractSettings']),
    ...mapGetters(['getAssets']),
    isDisabledSaveLowValueAssetsEdit() {
      for (let i = 0; i < this.formData.lowValueAssets.length; i++) {
        for (let j = 0; j < this.formData.lowValueAssets[i].periodSpendings.length; j++) {
          if (
            this.formData.lowValueAssets[i].periodSpendings[j].startDate === null
            || this.formData.lowValueAssets[i].periodSpendings[j].endDate === null
            || this.formData.lowValueAssets[i].periodSpendings[j].sum === null
          ) {
            return true;
          }
        }
      }

      return false;
    },
    settingsCurrencyCode() {
      return this.getContractSettings.currencyCode;
    },
    isDisabledAddPeriodSpendings() {
      for (let i = 0; i < this.formData.periodSpendings.length; i++) {
        if (
          this.formData.periodSpendings[i].startDate === null
          || this.formData.periodSpendings[i].endDate === null
          || this.formData.periodSpendings[i].sum === null
        ) {
          return true;
        }
      }

      return false;
    },
    isSelectedTypeException() {
      return this.formData.isExceptionToArticle3OfIFRS16 || this.formData.isIrrelevant || this.formData.isLowValue
        || this.formData.isShortTerm || this.formData.isVariableLeasePayments || this.formData.isNotRentContract;
    },
    date: {
      get() {
        if (this.formData.date === null) {
          return null;
        }
        return dateConverter.convertToDate(this.formData.date);
      },
      set(newVal) {
        if (newVal !== null) {
          this.formData.date = dateConverter.convertToString(newVal);
        } else {
          this.formData.date = null;
        }
      },
    },
    counterparties() {
      return this.getCounterpartiresForContract;
    },
    currentLessee: {
      get() {
        return this.formData.lesseeId;
      },
      set(newVal) {
        this.formData.lesseeId = +newVal;
      },
    },
  },
  methods: {
    setIsShowAssetsForAdd() {
      this.isShowAssetsForAdd = !!(this.formData.isLowValue || this.formData.isShortTerm || this.formData.isVariableLeasePayments);
    },
    updateFormData() {
      if (this.contract != null) {
        this.formData = _.cloneDeep(this.contract);
      }

      if (this.formData.lowValueAssets.length > 0 || this.formData.isLowValue || this.formData.isShortTerm || this.formData.isVariableLeasePayments) {
        this.isShowAssetsForAdd = true;
      }
    },
    addLessor() {
      this.formData.lessorIds.push(null);
      this.isDisabledAddLessor = true;
    },
    onSelectLessor(data) {
      this.formData.lessorIds[data.index] = data.value;
      this.isDisabledAddLessor = false;
    },
    closeContractEditingDialog() {
      this.$emit('close');
      this.formData = _.cloneDeep(this.contract);
    },
    deleteLessorItem(index) {
      this.formData.lessorIds.splice(index, 1);
      this.isDisabledAddLessor = false;
      this.counter++;
    },
    checkIsCounterpartyIntragroup(counterpartyId) {
      for (let i = 0; i < this.allCounterparties.length; i++) {
        if (this.allCounterparties[i].idInDB === counterpartyId) {
          return this.allCounterparties[i].isIntragroupTurnover;
        }
      }
    },
    newAssetCreatedForGroup(data) {
      const newAsset = this.getAssets.find(a => a.idInDB === data.new);
      const oldAsset = this.getAssets.find(a => a.idInDB === data.old);
      this.availableAssetIds.push(newAsset);

      const ckeckOldAsset = this.availableAssetIds.find(a => a.idInDB === data.old);
      if (ckeckOldAsset === null) {
        this.availableAssetIds.push(oldAsset);
      }
    },
    newAssetSelectedForGroup(data) {
      // Return previous asset to collection
      const oldAsset = this.getAssets.find(a => a.idInDB === data.old);
      const ckeckOldAsset = this.availableAssetIds.find(a => a.idInDB === data.old);
      if (ckeckOldAsset === null && oldAsset !== null) {
        this.availableAssetIds.push(oldAsset);
      }

      const choosenAsset = this.getAssets.find(a => a.idInDB === data.old);
      if (choosenAsset != null) {
        this.availableAssetIds.push(choosenAsset);
      }

      // remove new selected asset from collection if exist
      const neededAsset = this.availableAssetIds.find(a => a.idInDB === data.new);
      if (neededAsset !== null) {
        const neededAssetIndex = this.availableAssetIds.indexOf(neededAsset);
        this.availableAssetIds.splice(neededAssetIndex, 1);
      }
    },
    onDeleteAsset(asset) {
      for (let i = 0; i < this.formData.lowValueAssets.length; i++) {
        if (this.formData.lowValueAssets[i].assetId === asset) {
          this.formData.lowValueAssets.splice(i, 1);
        }
      }
      this.$store.commit('EDIT_ALTERNATIVE_CONTRACT_VALUE', {
        name: 'lowValueAssets',
        value: this.lowValueAssets,
      });
    },
    addLowValueAsset() {
      this.formData.lowValueAssets.push(LowValueAsset.createNew());
    },
    deletePeriodSpendingsItem(itemIndex) {
      this.formData.periodSpendings.splice(itemIndex, 1);
    },
    addPeriodSpendings() {
      this.formData.periodSpendings.push(PeriodSpendings.createNew());
    },
    onSubmit(e) {
      e.preventDefault();

      if (!this.validate()) return;

      if (this.formData.isLowValue) {
        this.formData.periodSpendings = [];
      } else {
        this.formData.lowValueAssets = [];
      }

      this.$emit('onSaveEditing', this.formData);
      this.$emit('close');
      this.formData = _.cloneDeep(this.contract);
    },
    validate() {
      this.validationErrors = [];
      this.validationPeriodSpendings = [];

      const contract = _.cloneDeep(this.formData);

      if (!contract.number) {
        this.validationErrors.push('number');
      }
      if (!contract.date) {
        this.validationErrors.push('date');
      }
      if (!contract.lesseeId) {
        this.validationErrors.push('currentLessee');
      }
      if (!this.arrayIsNotEmpty(contract.lessorIds)) {
        this.validationErrors.push('lessor');
      }

      if (!contract.isExceptionToArticle3OfIFRS16 && !contract.isIrrelevant && !contract.isLowValue
        && !contract.isShortTerm && !contract.isVariableLeasePayments && !contract.isNotRentContract) {
        this.validationErrors.push('typeException');
      }
      if (contract.isLowValue) {
        this.validateLowValueAssets(contract.lowValueAssets);
        return this.validationErrors.length <= 0 && !this.validationLowValueAsset.some(e => e.assetId || e.periodSpendings.some(p => p.length > 0));
      }
      this.validatePeriodSpendings(contract.periodSpendings);
      return this.validationErrors.length <= 0 && !this.validationPeriodSpendings.some(e => e.length > 0);
    },
    validatePeriodSpendings(periodSpendings) {
      this.validationPeriodSpendings = [];
      for (const [index, periodSpending] of periodSpendings.entries()) {
        this.$set(this.validationPeriodSpendings, index, []);
        if (!periodSpending.startDate) {
          this.validationPeriodSpendings[index].push('startDate');
        }
        if (!periodSpending.endDate) {
          this.validationPeriodSpendings[index].push('endDate');
        }
        if (periodSpending.sum === null || periodSpending.sum === undefined) {
          this.validationPeriodSpendings[index].push('sum');
        }
      }
      return this.validationPeriodSpendings;
    },
    validateLowValueAssets(lowValueAssets) {
      this.validationLowValueAsset = [];
      for (const lowValueAsset of lowValueAssets) {
        this.validationLowValueAsset.push({ assetId: !lowValueAsset.assetId, periodSpendings: this.validatePeriodSpendings(lowValueAsset.periodSpendings) });
      }
    },
    isFieldWithError(field, value) {
      return !value && this.validationErrors && this.validationErrors.includes(field);
    },
    arrayIsNotEmpty(array) {
      return Array.isArray(array) && array.length > 0 && array.every(a => !!a);
    },
  },
};
</script>
